import React, { CSSProperties, forwardRef, useMemo } from 'react';
import { useFocusRing, useHover, mergeProps, usePress } from 'react-aria';
import { genericMemo, isNumber } from '@direct-frontend/stdlib';

import { cn } from '../../lib/css/className/className';
import { useHStack } from '../../hooks/useStack';
import { VerticalAlignAddonSize } from '../../hooks/useVerticalAlign';
import { useInternalVerticalAlign } from '../../hooks/useVerticalAlign/useVerticalAlign';
import { HStack, StackJustifyContent, VStack } from '../Stack';
import { TextPreset } from '../TextGeneric/types';
import './Cell.css';
import './ControlType.css';
import './IconType.css';
import './ImageType.css';
import './LabelType.css';
import './CheckboxType.css';
import { useColorStatus } from '../../hooks/useColorStatus';
import { CellSizeContext } from '../Table/context';
import { SkinColor, SkinRole } from '../../lib/skin/getColorSkin/generated/types';
import { getColorSkin } from '../../lib/skin/getColorSkin/getColorSkin';

import { CellColor, CellProps } from './types';

const cls = cn('Cell');

type CellSizeConfig = {
    textSize: TextPreset;
    iconSize: VerticalAlignAddonSize;
};

type CellSizeConfigProps = Required<CellProps>['size'];

const cellSizeConfig: Record<CellSizeConfigProps, CellSizeConfig> = {
    s: {
        textSize: '12-16',
        iconSize: '12',
    },
    m: {
        textSize: '14-20',
        iconSize: '16',
    },
    l: {
        textSize: '16-20',
        iconSize: '20',
    },
};

const justifyContentConfig: Record<Required<CellProps>['align'], StackJustifyContent> = {
    center: 'center',
    right: 'flex-end',
    left: 'flex-start',
};

const skinMap: Record<CellColor, SkinColor | SkinRole> = {
    error: 'error',
    'error-low-contrast': 'red-low-contrast',
    success: 'success',
    'success-low-contrast': 'green-low-contrast',
    neutral: 'message-lc',
};

/**
 * Компонент для отрисовки базовой ячейки таблицы
 */
export const Cell = genericMemo(
    forwardRef<HTMLDivElement, CellProps>(function Cell(
        { children, className, 'data-testid': dataTestId, ...props },
        ref,
    ) {
        const { isFocusVisible } = useFocusRing({ within: true });
        const {
            type,
            alignItems,
            align = 'left',
            topPadding = true,
            bottomPadding = true,
            rightPadding = true,
            leftPadding = true,
            iconRight,
            size = 'm',
            isGrowable = true,
            height,
            width,
            onClick,
            onHoverStart,
            onHoverChange,
            onHoverEnd,
            addonBottom,
            withBorder,
            borderColor,
            borderStyle,
            isHighlighted,
        } = props;
        const tableSize = React.useContext(CellSizeContext);

        const cellSize = tableSize ?? size;

        const { textSize, iconSize } = cellSizeConfig[cellSize];

        const { hoverProps, isHovered } = useHover({ onHoverStart, onHoverChange, onHoverEnd });
        const { pressProps } = usePress({ onPress: onClick });

        const { addonClasses } = useInternalVerticalAlign({
            textSize,
            addonSize: iconSize,
        });

        const statusColor = ('statusColor' in props && props.statusColor) || undefined;
        const statusColorClass = useColorStatus({
            color: leftPadding ? statusColor : undefined,
            size: cellSize,
        });

        const justifyContent = justifyContentConfig[align];

        const stackClasses = useHStack({
            gap: '8',
            justifyContent,
        });

        const style = useMemo<CSSProperties | undefined>(() => {
            if (isNumber(height)) {
                return {
                    height: `${height}px`,
                };
            }

            return;
        }, [height]);

        const color = ('color' in props && props.color) || undefined;
        const skinColor = getColorSkin(color ? skinMap[color] : 'gray-cell', {});

        const cellContent = (
            <>
                {children}
                {Boolean(iconRight) && (
                    <VStack className={cls('iconRight', [addonClasses])} justifyContent="center">
                        {iconRight}
                    </VStack>
                )}
            </>
        );

        return (
            <div
                ref={ref}
                className={cls(
                    {
                        align,
                        alignItems,
                        focused: isFocusVisible,
                        fullWidth: width === 'full',
                        fullHeight: height === 'full',
                        topPadding: topPadding,
                        bottomPadding: bottomPadding,
                        rightPadding: rightPadding,
                        leftPadding: leftPadding,
                        hovered: isHovered,
                        size: cellSize,
                        grow: isGrowable,
                        withColor: Boolean(color),
                        type,
                        withBorder,
                        borderStyle,
                        borderColor,
                        highlighted: isHighlighted,
                        clickable: Boolean(onClick),
                    },
                    [className, stackClasses, skinColor, statusColorClass],
                )}
                data-testid={dataTestId}
                style={style}
                // Нужен tabindex, чтобы при клике на ячейку на неё реально переходил фокус
                // Иначе ломаются обработчики блюра на инпутах
                tabIndex={onClick ? 1 : undefined}
                {...mergeProps(hoverProps, onClick ? pressProps : {})}
            >
                {addonBottom ? (
                    <VStack className={cls('content')}>
                        <HStack>{cellContent}</HStack>
                        <div>{addonBottom}</div>
                    </VStack>
                ) : (
                    <>{cellContent}</>
                )}
            </div>
        );
    }),
);
